import { Component, Input, OnInit, Renderer2, NgZone, Inject, ChangeDetectorRef, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import gsap from 'gsap';
import { GuidedTourService } from '../../../shared/services/guided-tour.service';
import { Router } from '@angular/router';

export interface TourStep {
  selector: string;
  topic: string;
  description: string;
  stepNumber:any;
}

@Component({
    selector: 'app-tour-guide',
    templateUrl: './tour-guide.component.html',
    styleUrls: ['./tour-guide.component.scss'],
    standalone: false
})
export class TourGuideComponent implements OnInit, AfterViewInit {
  @Input() steps: TourStep[] = [];
  @Input() announcementId:any='';
  @ViewChild('maskEl', { static: false }) maskEl!: ElementRef;
  @ViewChild('descEl', { static: false }) descEl!: ElementRef;
  showTour = false;
  descVisible = false;
  maskStyle: any = {};
  descStyle: any = {};
  topic = '';
  description = '';
  dontShowAgain = false;
  stepIndex = 0;
  elementFound = false;
  responseData: any;
  successMsg: any;
  announcementDetails: any;
  errorMsg: any;
  
  constructor(
    private tourService: GuidedTourService,
    private renderer: Renderer2,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    public router: Router,
  ) {}

  ngAfterViewInit() {
    // Initial animation setup if needed
  }

  ngOnInit() {
    this.tourService.showTour$.subscribe(show => {
      this.showTour = show;      
      if (show) {
        this.zone.runOutsideAngular(() => setTimeout(() => {
          this.positionMask();
          this.cdr.detectChanges();
        }, 100));
      }
    });
    this.tourService.currentStep$.subscribe(idx => {      
      this.stepIndex = idx;
      if (this.showTour) {
        this.zone.runOutsideAngular(() => setTimeout(() => {
          this.positionMask();
          this.cdr.detectChanges();
        }, 100));
      }
    });
  }

  async positionMask() {
    const step = this.steps[this.stepIndex];
    if (!step) {
      console.warn('[TourGuide] No step found for index', this.stepIndex);
      this.elementFound = false;
      this.cdr.detectChanges();
      return;
    }
    const el = document.querySelector(step.selector) as HTMLElement;
    if (el) {
      this.elementFound = true;
      this.descVisible = false;
      this.cdr.detectChanges();
      const rect = el.getBoundingClientRect();
      // Animate mask
      const maskTarget = {
        x: rect.left - 8,
        y: rect.top - 8,
        w: rect.width + 16,
        h: rect.height + 16
      };
      let maskAnimPromise: Promise<void> = Promise.resolve();
      if (this.maskEl && this.maskEl.nativeElement) {
        maskAnimPromise = new Promise<void>(resolve => {
          gsap.to(this.maskEl.nativeElement, {
            x: maskTarget.x,
            y: maskTarget.y,
            width: maskTarget.w,
            height: maskTarget.h,
            duration: 0.5,
            ease: 'power2.inOut',
            overwrite: 'auto',
            onComplete: resolve
          });
        });
      }
      // Position description below unless too close to bottom, then to the right
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      let descStyle;
      let descTarget = { top: 0, left: 0 };
      if (spaceBelow < 400) {
        descTarget.top = rect.top;
        descTarget.left = rect.right + 32;
        if (this.descEl && this.descEl.nativeElement) {
          this.renderer.addClass(this.descEl.nativeElement, 'tour-desc-right');
        }
        descStyle = {
          top: `${descTarget.top}px`,
          left: `${descTarget.left}px`,
          maxWidth: '30vw',
        };
      } else {
        descTarget.top = rect.bottom + 24;
        descTarget.left = rect.left - 8;
        descStyle = {
          top: `${descTarget.top}px`,
          left: `${descTarget.left}px`,
          maxWidth: '30vw',
        };
      }
      // Animate description position, but keep it hidden
      if (this.descEl && this.descEl.nativeElement) {
        gsap.set(this.descEl.nativeElement, { opacity: 0 });
        gsap.to(this.descEl.nativeElement, {
          top: descTarget.top + 16,
          left: descTarget.left,
          duration: 0.5,
          ease: 'power2.inOut',
          overwrite: 'auto',
          opacity: 0
        });
      }
      this.descStyle = descStyle;
      this.topic = step.topic;
      this.description = step.description;
      this.cdr.detectChanges();
      // After mask animation, fade in description
      setTimeout(() => {
        this.descVisible = true;
        if (this.descEl && this.descEl.nativeElement) {
          gsap.to(this.descEl.nativeElement, { opacity: 1, duration: 0.4, top: descTarget.top, ease: 'power2.in' });
        }
        this.cdr.detectChanges();
      }, 500);
    } else {
      this.elementFound = false;
      console.warn('[TourGuide] Element NOT found for selector', step.selector, 'at step', this.stepIndex);
      setTimeout(() => this.positionMask(), 300);
      return;
    }
  }

  isLastStep() {
    return this.stepIndex === this.steps.length - 1;
  }

  nextStep() {
    // Fade out description, then scroll and advance
    if (this.descEl && this.descEl.nativeElement) {
      gsap.to(this.descEl.nativeElement, { opacity: 0, duration: 0.3, ease: 'power2.inOut' });
    }
    this.descVisible = false;
    this.cdr.detectChanges();
    setTimeout(() => {
      const nextStepIndex = this.stepIndex + 1;
      if (nextStepIndex < this.steps.length) {
        const nextStep = this.steps[nextStepIndex];
        const el = document.querySelector(nextStep.selector) as HTMLElement;
        if (el) {
          const rect = el.getBoundingClientRect();
          // Scroll so that the element is 80px from the top of the viewport
          const scrollTo = window.scrollY + rect.top - 80;
          window.scrollTo({ top: scrollTo < 0 ? 0 : scrollTo, behavior: 'smooth' });
          setTimeout(() => {
            this.tourService.nextStep(this.steps.length);
          }, 300); // Wait for scroll animation
          return;
        }
      }
      this.tourService.nextStep(this.steps.length);
    }, 300);
  }

  skipTour() {
  //  this.saveTourPreference();
    this.savePrefrences('skip');
    this.endTour();
  }

  doneTour() {
     //  this.saveTourPreference();
    this.dontShowAgain = true;
    this.savePrefrences('done');
    this.endTour();
  }

  saveTourPreference() {
    // Save user preference in localStorage or a service
    localStorage.setItem('dontShowTourAgain', this.dontShowAgain ? 'true' : 'false');
  }

  endTour() {
    this.tourService.endTour();
  }


  //Actual code for the tour guide component
 
  savePrefrences(action?:any){
    let reqPayload = {
      userId: localStorage.getItem('userId'),
      announcementId: this.announcementId,
      dontShowFlag: this.dontShowAgain
    };
    this.tourService.saveAnnouncements(reqPayload).subscribe(response => {
      if(response.code==200){
         this.dontShowAgain = false;
         document.body.style.overflow = '';
        if(action=='done'){
        this.successMsg = response.result;
        setTimeout(() => {
          this.successMsg = '';
        }, 3000);
        }
      }
    });
  }
}
